import { Reader } from '../bytes/reader';
import { ParseError } from '../bytes/errors';
import { concat, equalBytes, fromBase64, utf8 } from '../bytes/encoding';
import { sha256 } from '../crypto/sha256';
import { readDigitallySigned } from '../tls/vectors';
import { verifyDigitallySigned, type PublicKey } from '../crypto/sig';
import { treeHeadSignatureInput } from '../rfc6962/sth';

/** One `— <name> <base64(keyId || sig)>` line of a signed note (c2sp.org/signed-note). */
export interface NoteSignature {
  name: string;
  keyId: Uint8Array;
  body: Uint8Array;
  line: string;
}

/** A parsed c2sp.org/tlog-checkpoint. */
export interface Checkpoint {
  origin: string;
  treeSize: number;
  rootHash: Uint8Array;
  extensionLines: string[];
  signatures: NoteSignature[];
  /** The signed text (everything up to and including the blank separator's leading newline). */
  text: string;
  raw: string;
}

const SIG_PREFIX = '— '; // em dash + space

export function parseCheckpoint(raw: string): Checkpoint {
  const sep = raw.indexOf('\n\n');
  if (sep === -1) throw new ParseError('checkpoint: missing blank line before signatures');
  const text = raw.slice(0, sep + 1);
  const lines = text.split('\n');
  lines.pop(); // trailing empty element after the final newline
  if (lines.length < 3) throw new ParseError('checkpoint: fewer than three lines of text');
  const [origin, sizeLine, hashLine, ...extensionLines] = lines as [string, string, string, ...string[]];
  if (!origin) throw new ParseError('checkpoint: empty origin line');
  if (!/^(0|[1-9][0-9]*)$/.test(sizeLine)) throw new ParseError(`checkpoint: bad tree size "${sizeLine}"`);
  const treeSize = Number(sizeLine);
  if (!Number.isSafeInteger(treeSize)) throw new ParseError('checkpoint: tree size too large');
  const rootHash = fromBase64(hashLine);
  if (rootHash.length !== 32) throw new ParseError('checkpoint: root hash is not 32 bytes');
  if (extensionLines.some((l) => l.length === 0)) throw new ParseError('checkpoint: empty extension line');

  const signatures: NoteSignature[] = [];
  for (const line of raw.slice(sep + 2).split('\n')) {
    if (line === '') continue;
    if (!line.startsWith(SIG_PREFIX)) throw new ParseError(`checkpoint: malformed signature line "${line.slice(0, 40)}"`);
    const rest = line.slice(SIG_PREFIX.length);
    const sp = rest.indexOf(' ');
    if (sp <= 0) throw new ParseError('checkpoint: signature line without key name');
    const name = rest.slice(0, sp);
    const blob = fromBase64(rest.slice(sp + 1));
    if (blob.length < 5) throw new ParseError('checkpoint: signature too short');
    signatures.push({ name, keyId: blob.subarray(0, 4), body: blob.subarray(4), line });
  }
  if (signatures.length === 0) throw new ParseError('checkpoint: no signature lines');
  return { origin, treeSize, rootHash, extensionLines, signatures, text, raw };
}

/** static-ct-api key ID for the RFC 6962 note signature: SHA-256(name || 0x0A || 0x05 || logId)[:4]. */
export async function ctKeyId(name: string, logId: Uint8Array): Promise<Uint8Array> {
  const h = await sha256(concat(utf8(name), new Uint8Array([0x0a, 0x05]), logId));
  return h.subarray(0, 4);
}

export interface CheckpointVerification {
  ok: boolean;
  /** Timestamp (ms) carried inside the CT signature body, when a matching signature was found. */
  timestamp?: number;
  /** The signature line that matched the log's name and key ID. */
  signature?: NoteSignature;
  error?: string;
}

/**
 * Finds the log's own signature (name == origin AND key ID matches — logs add grease lines with the
 * right name but a bogus key ID) and verifies the embedded RFC 6962 TreeHeadSignature.
 */
export async function verifyCheckpoint(cp: Checkpoint, logId: Uint8Array, key: PublicKey): Promise<CheckpointVerification> {
  const keyId = await ctKeyId(cp.origin, logId);
  const candidates = cp.signatures.filter((s) => s.name === cp.origin && equalBytes(s.keyId, keyId));
  if (candidates.length === 0) {
    return { ok: false, error: `no signature by "${cp.origin}" with the expected key ID ${keyId.toHex()}` };
  }
  let lastError = 'signature did not verify';
  for (const sig of candidates) {
    try {
      const r = new Reader(sig.body);
      const timestamp = r.u64n();
      const ds = readDigitallySigned(r);
      r.expectEnd('RFC6962NoteSignature');
      const res = await verifyDigitallySigned(key, ds, treeHeadSignatureInput(timestamp, cp.treeSize, cp.rootHash));
      if (res.ok) return { ok: true, timestamp, signature: sig };
      if (res.error) lastError = res.error;
    } catch (e) {
      lastError = e instanceof Error ? e.message : String(e);
    }
  }
  return { ok: false, error: lastError };
}

/** Serialises a checkpoint back into note text (without signatures) — for display/diffing. */
export function checkpointText(origin: string, treeSize: number, rootHash: Uint8Array, extensionLines: string[] = []): string {
  return [origin, String(treeSize), rootHash.toBase64(), ...extensionLines].join('\n') + '\n';
}
